import React, { useState, useMemo } from "react";
import { Layout } from "@/components/Layout";
import { PageHero, SectionHeading, RequestInfoButton } from "@/components/common";
import { useEnquiry } from "@/context/EnquiryContext";
import { useSEO } from "@/lib/useSEO";
import { CAREERS } from "@/config/site";
import { MapPin, Briefcase, Building2, ArrowRight } from "lucide-react";

const orgFilters = ["All", "Atmasamman Group", "ATS", "ASAI", "AIMT", "Atmasamman Foundation"];
const categoryFilters = ["All", "Experienced Professionals", "Freshers", "Internships"];

export default function Careers() {
  const { openEnquiry } = useEnquiry();
  const [org, setOrg] = useState("All");
  const [cat, setCat] = useState("All");
  useSEO({
    title: "Careers | Atmasamman Group",
    description: "Explore career and internship opportunities across technology, AI, education and social impact at Atmasamman Group.",
    path: "/careers",
  });

  const filtered = useMemo(() => {
    return CAREERS.positions.filter((p) =>
      (org === "All" || p.organization === org) && (cat === "All" || p.category === cat)
    );
  }, [org, cat]);

  return (
    <Layout>
      <PageHero eyebrow="Careers" title="Careers Across Atmasamman Group"
        subtitle="Explore opportunities across technology, AI, education and social impact.">
        <div className="flex flex-wrap gap-3">
          <button onClick={() => openEnquiry({ interest: "Career Opportunities" })} data-testid="careers-hero-career-btn"
            className="rounded-xl bg-orange-500 px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-orange-600 md:text-base">Explore Careers</button>
          <button onClick={() => openEnquiry({ interest: "Internship Opportunities" })} data-testid="careers-hero-internship-btn"
            className="rounded-xl border border-white/20 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition-all hover:border-orange-500/50 md:text-base">Explore Internships</button>
        </div>
      </PageHero>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* filters */}
          <div className="flex flex-col gap-5">
            <div>
              <p className="mb-2 font-mono text-xs uppercase tracking-wider text-slate-500">Organization</p>
              <div className="flex flex-wrap gap-2" data-testid="careers-org-filters">
                {orgFilters.map((f) => (
                  <button key={f} onClick={() => setOrg(f)} data-testid={`filter-org-${f.split(" ")[0].toLowerCase()}`}
                    className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${org === f ? "bg-orange-500 text-white" : "border border-slate-300 text-slate-600 hover:border-orange-400"}`}>{f}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 font-mono text-xs uppercase tracking-wider text-slate-500">Category</p>
              <div className="flex flex-wrap gap-2" data-testid="careers-cat-filters">
                {categoryFilters.map((f) => (
                  <button key={f} onClick={() => setCat(f)} data-testid={`filter-cat-${f.split(" ")[0].toLowerCase()}`}
                    className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${cat === f ? "bg-[#0B132B] text-white" : "border border-slate-300 text-slate-600 hover:border-orange-400"}`}>{f}</button>
                ))}
              </div>
            </div>
          </div>

          {/* listings */}
          <div className="mt-12">
            {filtered.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-[#F8FAFC] p-12 text-center" data-testid="careers-empty">
                <Briefcase className="mx-auto h-10 w-10 text-slate-400" />
                <p className="mt-4 text-base text-slate-600">No current opportunities are available in this category. Please check again later.</p>
                <div className="mt-6">
                  <RequestInfoButton interest="Career Opportunities" label="Register Your Interest" testId="careers-empty-request" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {filtered.map((p) => (
                  <div key={p.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8" data-testid={`job-card-${p.id}`}>
                    <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                      <span className="rounded-full bg-orange-500/10 px-3 py-1 text-orange-600">{p.category}</span>
                      <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {p.organization}</span>
                    </div>
                    <h3 className="mt-3 font-display text-xl font-bold text-slate-900">{p.title}</h3>
                    <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-500">
                      <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {p.location}</span>
                      <span className="inline-flex items-center gap-1"><Briefcase className="h-4 w-4" /> {p.employmentType}</span>
                    </div>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{p.description}</p>
                    <button onClick={() => openEnquiry({ interest: "Career Opportunities" })} data-testid={`job-apply-${p.id}`}
                      className="mt-5 inline-flex w-fit items-center gap-2 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange-600">
                      Apply <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
